/**
 * Generic localStorage helpers with persona-scoped keys
 * Provides utilities for managing persona-specific data persistence
 */

export type PersonaType = "FOUNDER" | "INVESTOR" | "ACCELERATOR" | "ACADEMIA" | "DFI_GOVT" | "GENERIC";

/**
 * Generate persona-specific storage key
 * @param persona - User persona type
 * @param key - Storage key name
 * @returns Scoped storage key in format: {persona}_{key}
 */
export const getStorageKey = (persona: PersonaType, key: string): string => {
  return `${persona.toLowerCase()}_${key}`;
};

/**
 * Get selected entity/organization ID for a persona
 * @param persona - User persona type
 * @returns Stored entity ID or null
 */
export const getSelectedEntityId = (persona: PersonaType): string | null => {
  const key = getStorageKey(persona, "selected_entity_id");
  return localStorage.getItem(key);
};

/**
 * Persist selected entity/organization ID for a persona
 * @param persona - User persona type
 * @param id - Entity/organization ID to store
 */
export const setSelectedEntityId = (persona: PersonaType, id: string): void => {
  const key = getStorageKey(persona, "selected_entity_id");
  localStorage.setItem(key, id);
};

/**
 * Check if modal should be shown based on 24h cooldown logic
 * @param storageKey - Full storage key for the modal timestamp
 * @returns true if modal should be shown, false if in cooldown period
 */
export const shouldShowModal = (storageKey: string): boolean => {
  const closedAt = localStorage.getItem(storageKey);

  if (!closedAt) {
    return true;
  }

  const closedTime = new Date(closedAt).getTime();
  const now = new Date().getTime();
  const hoursSinceClosed = (now - closedTime) / (1000 * 60 * 60);

  // Show modal if more than 24 hours have passed
  return hoursSinceClosed > 24;
};

/**
 * Dismiss a modal and set 24h cooldown timestamp
 * @param storageKey - Full storage key for the modal timestamp
 */
export const dismissModal = (storageKey: string): void => {
  localStorage.setItem(storageKey, new Date().toISOString());
};

/**
 * Get modal storage key for pending claims
 * @param persona - User persona type
 * @returns Storage key for pending claims modal timestamp
 */
export const getPendingClaimsModalKey = (persona: PersonaType): string => {
  return getStorageKey(persona, "pending_claims_closed_at");
};

/**
 * Get modal storage key for no claims
 * @param persona - User persona type
 * @returns Storage key for no claims modal timestamp
 */
export const getNoClaimsModalKey = (persona: PersonaType): string => {
  return getStorageKey(persona, "no_claims_closed_at");
};
